import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { EditableSlotGroup, useSlotGroup } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ---------------------------------------------------------------------------
// FAQ — an accordion of question/answer pairs.
//
// The whole Q&A set lives in ONE slot group (`faq.items`) so an editor manages
// every question and answer from a single grouped panel. Richtext slot-group
// fields are auto-resolved by the runtime (embedded field tokens + sanitize),
// so we render each resolved value directly. Items whose question resolves
// empty are hidden, letting per-property editors trim the list.
//
// Uses native <details>/<summary> so expand/collapse works with zero JS
// (SSG-friendly + accessible).
// ---------------------------------------------------------------------------

const faqItemsDef: SlotGroupDefinition = {
  label: 'Questions & Answers',
  description: 'Each question is paired with its answer. Clear a question to hide that row.',
  fields: {
    q1_question: {
      label: 'Question 1',
      type: 'richtext',
      defaultValue: '<p>What are your office hours?</p>',
    },
    q1_answer: {
      label: 'Answer 1',
      type: 'richtext',
      defaultValue:
        '<p>Our leasing office is open Monday through Friday and select weekend hours. Reach out any time and our team will be happy to help you schedule a tour or answer questions.</p>',
    },
    q2_question: {
      label: 'Question 2',
      type: 'richtext',
      defaultValue: '<p>How do I apply for an apartment?</p>',
    },
    q2_answer: {
      label: 'Answer 2',
      type: 'richtext',
      defaultValue:
        '<p>You can apply online in just a few minutes using our secure application. Once submitted, a member of our team will follow up to walk you through the next steps.</p>',
    },
    q3_question: {
      label: 'Question 3',
      type: 'richtext',
      defaultValue: '<p>Are pets allowed?</p>',
    },
    q3_answer: {
      label: 'Answer 3',
      type: 'richtext',
      defaultValue:
        '<p>We are a pet-friendly community and welcome cats and dogs. Breed and weight restrictions, along with any applicable pet fees or deposits, are listed on our Pet Policy page.</p>',
    },
    q4_question: {
      label: 'Question 4',
      type: 'richtext',
      defaultValue: '<p>What utilities are included in the rent?</p>',
    },
    q4_answer: {
      label: 'Answer 4',
      type: 'richtext',
      defaultValue:
        '<p>Included utilities vary by floor plan and lease term. Contact our leasing office and we will provide a full breakdown of what is covered and what residents are responsible for.</p>',
    },
    q5_question: {
      label: 'Question 5',
      type: 'richtext',
      defaultValue: '<p>Is parking available?</p>',
    },
    q5_answer: {
      label: 'Answer 5',
      type: 'richtext',
      defaultValue:
        '<p>Yes — a range of parking options is available for residents and guests. Ask our team about current availability and any associated fees.</p>',
    },
    q6_question: {
      label: 'Question 6',
      type: 'richtext',
      defaultValue: '<p>How do I submit a maintenance request?</p>',
    },
    q6_answer: {
      label: 'Answer 6',
      type: 'richtext',
      defaultValue:
        '<p>Residents can submit maintenance requests through the resident portal at any time. For urgent or emergency issues, please call the leasing office directly.</p>',
    },
  },
};

// The six (question field, answer field) pairs, in display order.
const FAQ_ROWS: Array<{ q: string; a: string }> = [
  { q: 'q1_question', a: 'q1_answer' },
  { q: 'q2_question', a: 'q2_answer' },
  { q: 'q3_question', a: 'q3_answer' },
  { q: 'q4_question', a: 'q4_answer' },
  { q: 'q5_question', a: 'q5_answer' },
  { q: 'q6_question', a: 'q6_answer' },
];

function hasValue(val: unknown): boolean {
  if (val === null || val === undefined) return false;
  // Treat richtext that is only empty tags / whitespace as "no value".
  const text = String(val)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, '')
    .trim();
  return text !== '';
}

export function FAQ() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const items = useSlotGroup('faq.items', faqItemsDef);

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up py-16 md:py-24 bg-[var(--vibe-color-background)]"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableSlotGroup groupPath="faq.items" definition={faqItemsDef}>
          <div className="faq-list">
            {FAQ_ROWS.map((row) => {
              const question = items[row.q];
              const answer = items[row.a];
              if (!hasValue(question)) return null;
              return (
                <details key={row.q} className="faq-item">
                  <summary className="faq-summary">
                    <span
                      className="faq-question faq-heading-font"
                      dangerouslySetInnerHTML={{ __html: question ?? '' }}
                    />
                    <span aria-hidden="true" className="faq-icon" />
                  </summary>
                  <div
                    className="faq-answer faq-body-font"
                    dangerouslySetInnerHTML={{ __html: answer ?? '' }}
                  />
                </details>
              );
            })}
          </div>
        </EditableSlotGroup>
      </div>

      <style>{`
        /* Match HeroSolidBG's page title (.hero-solid-title-font): Oswald at
           normal weight, applied to the element and all rich-text children. */
        .faq-heading-font,
        .faq-heading-font * {
          font-family: 'Oswald', 'Arial Narrow', system-ui, sans-serif;
          font-weight: 400;
        }
        /* Match HeroSolidBG's blurb (.hero-solid-blurb-font): Space Mono at
           normal weight/style. */
        .faq-body-font,
        .faq-body-font * {
          font-family: 'Space Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
          font-weight: 400;
          font-style: normal;
        }

        .faq-list {
          display: flex;
          flex-direction: column;
          border-top: 1px solid #e8e8ea;
        }

        .faq-item {
          border-bottom: 1px solid #e8e8ea;
        }

        .faq-summary {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1.5rem;
          padding: 1.35rem 0.25rem;
          cursor: pointer;
          list-style: none;
          user-select: none;
        }
        /* Hide the default disclosure triangle across browsers. */
        .faq-summary::-webkit-details-marker {
          display: none;
        }

        .faq-question {
          font-size: 1.05rem;
          line-height: 1.4;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: var(--vibe-color-text);
        }
        .faq-question p {
          margin: 0;
        }

        /* +/- indicator built from two bars; the vertical bar collapses when
           the item is open, leaving a minus sign. */
        .faq-icon {
          position: relative;
          flex: 0 0 auto;
          width: 16px;
          height: 16px;
        }
        .faq-icon::before,
        .faq-icon::after {
          content: '';
          position: absolute;
          background: var(--vibe-color-primary);
          transition: transform 0.25s ease, opacity 0.25s ease;
        }
        .faq-icon::before {
          top: 50%;
          left: 0;
          width: 100%;
          height: 2px;
          transform: translateY(-50%);
        }
        .faq-icon::after {
          left: 50%;
          top: 0;
          width: 2px;
          height: 100%;
          transform: translateX(-50%);
        }
        .faq-item[open] .faq-icon::after {
          transform: translateX(-50%) scaleY(0);
          opacity: 0;
        }

        .faq-answer {
          padding: 0 0.25rem 1.5rem;
          font-size: 0.95rem;
          line-height: 1.7;
          color: var(--vibe-color-text-muted);
        }
        .faq-answer p {
          margin: 0 0 0.75rem;
        }
        .faq-answer p:last-child {
          margin-bottom: 0;
        }
        .faq-answer a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }
        .faq-answer ul {
          list-style: disc;
          padding-left: 1.25rem;
          margin: 0 0 0.75rem;
        }
        .faq-answer li {
          margin-bottom: 0.35rem;
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'FAQ',
  displayName: 'FAQ Accordion',
  tokens: {},
  slotGroups: {
    'faq.items': faqItemsDef,
  },
};

(FAQ as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FAQ;
